import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ComponentLoader, InlineLoader, Layout, MetricItemAdvance, Text, WidgetDivider } from '@netapp/bxp-design-system-react';
import { SuccessIcon, NoticeTriangleIcon } from '@netapp/bxp-style/react-icons/Notification';
import { configurations as i18nNamespace } from '../../../../i18n/namespace';
import styles from '../../style.module.scss';
import {
    PROXY_OPERATION_POLL_INTERVAL_MS,
    clearPendingProxyOperation,
    fetchProxyState,
    getProxyCurrentConfig,
    getProxyOperation,
    isFailedProxyStatus,
    isStaleProxyOperation,
    isTerminalProxyOperation,
    readPendingProxyOperation,
    readProxyState,
    selectHasCertificate,
} from './proxyConfiguration';

const StatusMetric = ({ label, value, ValueIcon, valueIconColor }) => (
    <MetricItemAdvance
        className={styles.metricPaddingRight5}
        label={label}
        labelTooltip={label}
        valueSize="small"
        value={value}
        valueTooltip={value}
        ValueIcon={ValueIcon}
        valueIconColor={valueIconColor}
        valueClassName={styles.valueLineHeight24}
    />
);

// `outcome.isFailure` is set for a FAILED operation and when the state response
// has no operation. `outcome.message` is the reason found in that response.
const notifyProxyOperationResult = (outcome, dispatch, t) => {
    const pendingOperation = readPendingProxyOperation();
    if (!pendingOperation) {
        return;
    }
    clearPendingProxyOperation();
    if (outcome.isFailure) {
        dispatch({
            type: 'NOTIFICATIONS:ERROR',
            payload: {
                message: outcome.message || t(pendingOperation.failureMessageKey),
                key: pendingOperation.notificationKey,
                showGlobal: true,
            },
        });
        return;
    }
    dispatch({
        type: 'NOTIFICATIONS:SUCCESS',
        payload: {
            message: t(pendingOperation.successMessageKey),
            key: pendingOperation.notificationKey,
            showGlobal: true,
        },
    });
};

const ProxyCardStatus = ({ onConfiguredChange }) => {
    const { t } = useTranslation(i18nNamespace);
    const dispatch = useDispatch();
    const [proxyConfig, setProxyConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(() => !readPendingProxyOperation());
    const [isConfiguring, setIsConfiguring] = useState(() => Boolean(readPendingProxyOperation()));
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        let isCancelled = false;
        let isSettled = false;
        let intervalId = null;

        const stopPolling = () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const showProxyConfig = config => {
            setProxyConfig(config);
            setErrorMessage(null);
            setIsConfiguring(false);
            setIsLoading(false);
        };

        // The proxy settings are read from `currentState.value` of the state GET.
        const loadProxy = async () => {
            try {
                const stateDocument = await fetchProxyState();
                if (!isCancelled) {
                    showProxyConfig(getProxyCurrentConfig(stateDocument));
                }
            } catch {
                if (!isCancelled) {
                    setProxyConfig(null);
                    setErrorMessage(t('failed_to_fetch_proxy'));
                    setIsConfiguring(false);
                    setIsLoading(false);
                }
            }
        };

        const finishOperation = async (outcome, currentConfig = null) => {
            if (isCancelled || isSettled) {
                return;
            }
            isSettled = true;
            stopPolling();
            // The card is mounted only on the console configurations page, so a
            // terminal result reached here is shown to a user who is on that page.
            notifyProxyOperationResult(outcome, dispatch, t);
            if (currentConfig) {
                showProxyConfig(currentConfig);
                return;
            }
            await loadProxy();
        };

        const readAndApplyProxyState = async () => {
            // Same state GET the edit page used. Polls until SUCCEEDED (or FAILED).
            const {
                stateDocument,
                errorMessage: stateErrorMessage,
                currentConfig,
                retry,
            } = await readProxyState();
            // Nothing is applied after unmount, or once the operation has settled.
            if (isCancelled || isSettled) {
                return;
            }
            if (retry) {
                // No response at all, so keep the configuring status and try again.
                setIsConfiguring(true);
                setIsLoading(false);
                return;
            }
            if (!stateDocument) {
                // `operation` could not be found in the response: stop polling and
                // report the error the response carries.
                await finishOperation({ isFailure: true, message: stateErrorMessage }, currentConfig);
                return;
            }
            const operation = getProxyOperation(stateDocument);
            if (isStaleProxyOperation(operation, readPendingProxyOperation())) {
                // The previous change's operation, not the one just started: it must
                // not end the wait or raise a notification. Keep polling.
                setIsConfiguring(true);
                setIsLoading(false);
                return;
            }
            const stateConfig = getProxyCurrentConfig(stateDocument);
            if (isTerminalProxyOperation(stateDocument)) {
                // SUCCEEDED or FAILED: `currentState.value` is the proxy to show.
                await finishOperation({
                    // FAILED or PRECHECK_FAILED: currentState is the proxy still in effect.
                    isFailure: isFailedProxyStatus(operation.status),
                    message: operation.failure?.message,
                }, stateConfig);
                return;
            }
            setProxyConfig(stateConfig);
            setIsConfiguring(true);
            setErrorMessage(null);
            setIsLoading(false);
        };

        // Only one state GET is in flight at a time. The interval keeps ticking, but
        // a tick that finds the previous GET still running is skipped. Without this,
        // a GET slower than the poll interval could resolve after a newer one had
        // already settled the operation and put the card back on "Configuring...".
        let isPollInFlight = false;
        const pollProxyOperation = async () => {
            if (isPollInFlight || isCancelled || isSettled) {
                return;
            }
            isPollInFlight = true;
            try {
                await readAndApplyProxyState();
            } finally {
                isPollInFlight = false;
            }
        };

        if (readPendingProxyOperation()) {
            pollProxyOperation();
            intervalId = setInterval(pollProxyOperation, PROXY_OPERATION_POLL_INTERVAL_MS);
        } else {
            loadProxy();
        }

        return () => {
            isCancelled = true;
            stopPolling();
        };
        // `t` is only used for notification copy. Leaving it out avoids
        // restarting the poll when the translator identity changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const proxyType = proxyConfig?.proxy_type?.toUpperCase();
    const isExplicit = proxyType === 'EXPLICIT';
    const isTransparent = proxyType === 'TRANSPARENT';
    const isConfigured = isExplicit || isTransparent;

    useEffect(() => {
        if (!isLoading) {
            onConfiguredChange?.(isConfigured, proxyConfig, errorMessage, isConfiguring);
        }
    }, [errorMessage, isConfigured, isConfiguring, isLoading, onConfiguredChange, proxyConfig]);

    if (isLoading) {
        return <ComponentLoader className={styles.cardStatusLoader} />;
    }

    if (isConfiguring) {
        return (
            <div className={styles.flexAlignCenter}>
                <InlineLoader className={styles.marginRight6} />
                <Text level={14}>{t('configuring_proxy')}</Text>
            </div>
        );
    }

    if (!isConfigured) {
        return null;
    }

    const httpProxy = proxyConfig?.proxy_url?.replace(/^(https?):\/\//i, '') || t('n/a');
    const hasCertificate = selectHasCertificate(proxyConfig);

    return (
        <Layout.Grid>
            <Layout.GridItem lg={4} className={styles.flexRowAlignEnd}>
                <StatusMetric
                    label={t('proxy_type')}
                    value={t(isExplicit ? 'explicit_proxy_type' : 'transparent_proxy_type')}
                    ValueIcon={SuccessIcon}
                    valueIconColor="success"
                />
                {isExplicit && <WidgetDivider expand />}
            </Layout.GridItem>
            {isExplicit && (
                <>
                    <Layout.GridItem lg={4} className={styles.flexRowAlignEndPaddingLeft5}>
                        <StatusMetric
                            label={t(hasCertificate ? 'https_proxy' : 'http_proxy')}
                            value={httpProxy}
                        />
                        <WidgetDivider expand />
                    </Layout.GridItem>
                    <Layout.GridItem lg={4} className={styles.flexRowAlignEndPaddingLeft5}>
                        <StatusMetric
                            label={t('certificate')}
                            value={t(hasCertificate ? 'uploaded' : 'not_uploaded')}
                            ValueIcon={hasCertificate ? SuccessIcon : NoticeTriangleIcon}
                            valueIconColor={hasCertificate ? 'success' : 'warning'}
                        />
                    </Layout.GridItem>
                </>
            )}
        </Layout.Grid>
    );
};

export default React.memo(ProxyCardStatus);
